import { action, observable, computed } from 'mobx';
import { merge } from 'lodash';
import { falseNotZero } from '~/utils/lodash.utils';
import { FlowRevision } from '~/models/flow-revision.model';
import { CXFlow } from '../models/cx-flow.model';
import type { CXRootStore } from '../../stores/cx-root.store';
import {
    IFlowMinimalResponse,
    IFlowTransformedData,
    IPageResponse,
    ITransformedPageResponse,
    IVariableResponse,
    TBlockResponse
} from '../../../../types/server.types';
import { CXMinimalFlow } from '../models/CXMinimalFlow';
import { IFlow } from '~/types/model.types';
import { trackError } from '~/services/logger/error.utils';

export class CxFlowStore {
    rootStore: CXRootStore = null;
    @observable minimalFlow: CXMinimalFlow = null;
    @observable flow: CXFlow = null;

    constructor(rootStore: CXRootStore) {
        this.rootStore = rootStore;
    }

    @computed
    get isClient2faSmsEnabled() {
        return (
            this.minimalFlow?.client?.client_2fa_sms_enabled &&
            this.minimalFlow?.client?.phone &&
            this.minimalFlow?.require_authentication
        );
    }

    get isFullFlowLoaded() {
        return !!this.flow?._id;
    }

    // TODO: remove these methods in future itterations
    get variables() {
        return this.flow.activeRevision.variablesMap;
    }

    get blocks() {
        return this.flow.activeRevision.blocksMap;
    }

    get pages() {
        return this.flow.activeRevision.pagesMap;
    }

    @action
    load(
        flow: IFlow,
        pages: Record<string, ITransformedPageResponse>,
        blocks: Record<string, TBlockResponse>,
        variables: Record<string, IVariableResponse>
    ) {
        this.flow = this.transformFlowResponseToFlow(
            flow,
            pages,
            blocks,
            variables
        );
    }

    @action
    transformFlowResponseToFlow(
        flow: IFlow,
        pages: Record<string, ITransformedPageResponse>,
        blocks: Record<string, TBlockResponse>,
        variables: Record<string, IVariableResponse>
    ) {
        const flowRevision = new FlowRevision(
            flow.revision_id,
            flow._id,
            pages,
            blocks,
            variables,
            this.rootStore
        );

        const _flow = new CXFlow(flow, this.rootStore);
        _flow.setActiveRevision(flowRevision);
        return _flow;
    }

    @action
    loadMinimal(minimalFlow: IFlowMinimalResponse) {
        this.minimalFlow = new CXMinimalFlow(minimalFlow);
    }

    @action
    setVariable(variableId: string, value: any, isPreview: boolean = false) {
        /*
            Variables are created on the server side. If a question was created in the builder
            it won't have a variable_id and won't exist in this.variables until next refresh
            This is a problem in preview mode, because it calls this function
            What this condition is saying - if you are in preview mode, it's ok for a variable not to exists in this.variables

            Other possible solution:
            Have the variable_id be generated on the client side and passed to the BE.
            This felt like a bit of an overkill for this issue, but might be required in the future
        */
        if (!this.flow.activeRevision.variablesMap[variableId] && isPreview) {
            return;
        }

        if (!this.flow.activeRevision.variablesMap[variableId]) {
            trackError({
                error: new Error('Tried setting a non existing variable'),
                context: {
                    variableId,
                    value,
                    flowId: this.flow._id
                }
            });
            return false;
        }

        this.flow.activeRevision.variablesMap[variableId].raw_value = value;
        this.flow.activeRevision.variablesMap[variableId].value = value;
        return true;
    }

    @action
    setVariableValue(
        variableId: string,
        value: any,
        isPreview: boolean = false
    ) {
        if (!this.flow.activeRevision.variablesMap[variableId] && isPreview) {
            return;
        }

        this.flow.activeRevision.setVariableValue(variableId, value);
    }

    @action
    updateFlowProps(flow: CXFlow, propsToUpdate: Partial<CXFlow>) {
        if (
            Object.keys(propsToUpdate).includes('pages') ||
            Object.keys(propsToUpdate).includes('blocks') ||
            Object.keys(propsToUpdate).includes('variables')
        ) {
            return;
        }

        Object.assign(flow, propsToUpdate);
    }

    @action
    syncSelectedFlowWithServerResponse(
        pages: IPageResponse[],
        blocks: TBlockResponse[],
        variables: IVariableResponse[]
    ) {
        (blocks || []).forEach(block => {
            const storeBlock = this.blocks[block.block_id];
            if (!storeBlock) {
                return;
            }

            storeBlock.mergeBlockAttributes(block, {
                connected_variable_id: true
            });
        });

        this.rootStore.flowViewUiStore.selectedFlow.updateVariables(variables);
    }

    // TODO: remove this method
    @action
    addVariable(variable: IVariableResponse) {
        this.flow.activeRevision.addVariables([variable]);
    }

    // TODO: remove this method
    @action
    removeVariable(variableId: string) {
        this.flow.activeRevision.removeVariables([{ variable_id: variableId }]);
    }

    @action
    mergeActiveRevisionUpdates(flowTransformedData: IFlowTransformedData) {
        const { flow, pages, variables } = flowTransformedData;
        this.flow = merge(this.flow, {
            ...flow,
            activeRevision: { pagesMap: pages, variablesMap: variables }
        });
    }

    selectedServicesForPreview() {
        return Object.values(this.variables).reduce((acc, variable) => {
            if (variable.value?.selected_services) {
                return [...acc, ...variable.value?.selected_services];
            }
            return acc;
        }, []);
    }

    serviceSelectionCustomQuantities() {
        return Object.values(this.variables).reduce((acc, variable) => {
            if (falseNotZero(variable.value?.quantity)) {
                return acc;
            }

            return [
                ...acc,
                {
                    block_id: variable.connected_block_id,
                    quantity: variable.value.quantity
                }
            ];
        }, []);
    }
    @action
    reset() {
        this.flow = null;
        this.minimalFlow = null;
    }
}
